import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/App";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ExternalLink, Search, Lock } from "lucide-react";
import ManufacturerCombobox from "@/pages/ManufacturerCombobox";
import { alientechVehicleTypeLabel, alientechFuelLabel, ecuTypeLabel } from "@/lib/labels";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import ToolLogo from "@/components/ToolLogo";
import GroupedCompatibilityList from "@/components/GroupedCompatibilityList";
import NewBadge from "@/components/NewBadge";
import MethodGlyph from "@/components/MethodGlyph";
import { ReactComponent as KlonbarIcon } from "@/assets/alientech-icons/klonbar.svg";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";

const PAGE_SIZE = 30;

export default function AlientechCompatibility() {
  const brandLogos = useBrandLogos();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [vehicleType, setVehicleType] = useState("all");
  const [ecuTypeFilter, setEcuTypeFilter] = useState("all");
  const [ecuMakerFilter, setEcuMakerFilter] = useState("all");
  const [brand, setBrand] = useState(null);
  const [facets, setFacets] = useState({
    vehicle_types: [],
    ecu_types: [],
    ecu_makers: [],
    brands: [],
    total: 0,
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (vehicleType !== "all") params.vehicle_type = vehicleType;
      if (ecuTypeFilter !== "all") params.ecu_type = ecuTypeFilter;
      if (ecuMakerFilter !== "all") params.ecu_maker = ecuMakerFilter;
      if (brand) params.brand = brand;
      const { data } = await api.get("/alientech/vehicles", { params });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      toast.error("Fehler beim Laden der Alientech-Liste");
    } finally {
      setLoading(false);
    }
  }, [page, search, vehicleType, ecuTypeFilter, ecuMakerFilter, brand]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/alientech/facets");
        setFacets(data);
      } catch (err) {
        console.warn("facets error", err);
      }
    })();
  }, []);

  // Debounce search + reflect in URL query
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
      if (searchInput) {
        setSearchParams({ search: searchInput }, { replace: true });
      } else if (searchParams.get("search")) {
        setSearchParams({}, { replace: true });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const showingRange = useMemo(() => {
    if (total === 0) return "0";
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return `${from}–${to}`;
  }, [page, total]);

  return (
    <div data-testid="alientech-page" className="space-y-6">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ToolLogo source="alientech" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <StatCard
            label="Einträge"
            value={facets.total.toLocaleString("de-DE")}
            testid="alientech-stat-total"
          />
          <StatCard
            label="Marken"
            value={facets.brands?.length ?? 0}
            testid="alientech-stat-brands"
          />
          <StatCard
            label="ECU-Typen"
            value={facets.ecu_types?.length ?? 0}
            testid="alientech-stat-ecu-types"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="alientech-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Suche Marke, Modell, ECU, Motorcode…"
            className="pl-9 bg-panel border-neutral-800 text-neutral-100 placeholder:text-neutral-500 focus-visible:ring-[#D32222]"
          />
        </div>
        <FilterSelect
          testid="alientech-filter-type"
          value={vehicleType}
          onChange={(v) => {
            setVehicleType(v);
            setPage(1);
          }}
          placeholder="Fahrzeugtyp"
          options={[
            { value: "all", label: "Alle Typen" },
            ...(facets.vehicle_types || []).map((v) => ({
              value: v,
              label: alientechVehicleTypeLabel(v),
              icon: <VehicleTypeIcon type={v} className="h-5 w-7" />,
            })),
          ]}
        />
        <FilterSelect
          testid="alientech-filter-ecu-type"
          value={ecuTypeFilter}
          onChange={(v) => {
            setEcuTypeFilter(v);
            setPage(1);
          }}
          placeholder="ECU-Typ"
          options={[
            { value: "all", label: "Alle ECU-Typen" },
            ...(facets.ecu_types || []).map((v) => ({ value: v, label: ecuTypeLabel(v) })),
          ]}
        />
        <FilterSelect
          testid="alientech-filter-ecu-maker"
          value={ecuMakerFilter}
          onChange={(v) => {
            setEcuMakerFilter(v);
            setPage(1);
          }}
          placeholder="ECU-Hersteller"
          options={[
            { value: "all", label: "Alle ECU-Hersteller" },
            ...(facets.ecu_makers || []).map((v) => ({ value: v, label: v })),
          ]}
        />
        <ManufacturerCombobox
          value={brand}
          onChange={(v) => {
            setBrand(v);
            setPage(1);
          }}
          options={facets.brands || []}
          placeholder="Alle Marken"
          testid="alientech-brand-combobox"
        />
      </div>

      <GroupedCompatibilityList
        provider="alientech"
        params={{ search: search || undefined, vehicle_type: vehicleType !== "all" ? vehicleType : undefined, ecu_type: ecuTypeFilter !== "all" ? ecuTypeFilter : undefined, ecu_maker: ecuMakerFilter !== "all" ? ecuMakerFilter : undefined, brand: brand || undefined }}
        renderRow={(row) => <Row row={row} search={search} brandLogos={brandLogos} grouped />}
      />

      {/* Legacy flat table kept hidden for compatibility */}
      <div className="hidden rounded-xl border border-neutral-800 bg-panel overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 pt-3 pb-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-800">
          <div className="col-span-3">Marke</div>
          <div className="col-span-2">Modell</div>
          <div className="col-span-2">Motor</div>
          <div className="col-span-2">ECU</div>
          <div className="col-span-1">Typ</div>
          <div className="col-span-2">Verbindungen</div>
        </div>

        {/* Rows */}
        <div data-testid="alientech-list" className="divide-y divide-neutral-800/70">
          {loading && items.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="px-4 py-4 animate-pulse grid grid-cols-12 gap-4"
              >
                <div className="col-span-3 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-1 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
              </div>
            ))
          ) : items.length === 0 ? (
            <div
              data-testid="alientech-empty-state"
              className="px-4 py-16 text-center text-neutral-500"
            >
              Keine Einträge gefunden. Passe deine Filter an oder starte den
              Alientech-Scraper.
            </div>
          ) : (
            items.map((row, idx) => (
              <Row key={idx} row={row} search={search} brandLogos={brandLogos} />
            ))
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="hidden flex-wrap items-center justify-between gap-4 pt-2">
        <div className="text-sm text-neutral-500">
          Zeige{" "}
          <span data-testid="alientech-range-label" className="text-neutral-200">
            {showingRange}
          </span>{" "}
          von{" "}
          <span data-testid="alientech-total-label" className="text-neutral-200">
            {total.toLocaleString("de-DE")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="alientech-prev-page"
            variant="outline"
            className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <div className="text-sm text-neutral-400 px-2">
            Seite <span className="text-neutral-100">{page}</span> /{" "}
            <span className="text-neutral-100">
              {totalPages.toLocaleString("de-DE")}
            </span>
          </div>
          <Button
            data-testid="alientech-next-page"
            variant="outline"
            className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-panel px-4 py-3 min-w-[110px]"
    >
      <div className="text-xl font-semibold text-[#D32222] tabular-nums">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  const selected = options.find((o) => o.value === value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="w-[190px] h-auto min-h-9 py-1.5 bg-panel border-neutral-800 text-neutral-100 focus:ring-[#D32222]"
      >
        {selected?.icon ? (
          <span className="inline-flex items-center gap-2">
            {selected.icon}
            {selected.label}
          </span>
        ) : (
          <SelectValue placeholder={placeholder} />
        )}
      </SelectTrigger>
      <SelectContent className="bg-panel border-neutral-800 text-neutral-100">
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            data-testid={`${testid}-opt-${o.value}`}
            className="focus:bg-neutral-800"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }) {
  if (text === undefined || text === null || text === "") return text;
  const str = String(text);
  const tokens = (query || "").split(/\s+/).filter(Boolean).map(escapeRegex);
  if (tokens.length === 0) return str;
  const re = new RegExp(`(${tokens.join("|")})`, "gi");
  const parts = str.split(re);
  if (parts.length === 1) return str;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark
        key={i}
        className="bg-[#D32222]/30 text-[#E13636] rounded-sm px-0.5"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function ConnectionBadge({ label, connection }) {
  if (!connection?.available) {
    return (
      <span
        title={`${label} nicht verfügbar`}
        className="inline-flex items-center justify-center opacity-20"
      >
        <MethodGlyph label={label} className="w-9 h-9" />
      </span>
    );
  }
  return (
    <span
      title={`${label} verfügbar${connection.clone ? " · klonbar" : ""}${connection.unlock_required ? " · Unlock nötig" : ""}`}
      className="relative inline-flex items-center justify-center"
    >
      <MethodGlyph label={label} className="w-9 h-9" />
      {connection.unlock_required && (
        <Lock className="absolute -top-1 -right-1 w-2.5 h-2.5 text-amber-400 bg-panel rounded-full p-0.5" />
      )}
    </span>
  );
}

function Row({ row, search, brandLogos, grouped = false }) {
  const connections = row.connections || {};
  const flags = row.flags || {};
  const logo = brandLogoFor(brandLogos, row.brand);

  return (
    <div
      data-testid="alientech-row"
      className="group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 transition"
    >
      {/* Marke */}
      <div className={grouped ? "hidden" : "md:col-span-3 flex items-center gap-3"}>
        {logo && (
          <img
            src={logo}
            alt={row.brand}
            className="h-16 w-16 object-contain rounded bg-panel border border-neutral-800 p-1.5 shrink-0"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">
            <Highlight text={row.brand} query={search} />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            <VehicleTypeIcon type={row.vehicle_type} className="inline-block h-4 w-5 mr-1 align-[-2px]" />
            {alientechVehicleTypeLabel(row.vehicle_type)}
          </div>
        </div>
      </div>
      {/* Modell */}
      <div className="md:col-span-2">
        <div className="text-neutral-100">
          {grouped ? (row.version || "—") : row.model ? (
            <Highlight text={row.model} query={search} />
          ) : (
            "—"
          )}
        </div>
        <div className="text-xs text-neutral-500">
          {grouped ? (row.model_year || "") : ([row.version, row.model_year].filter(Boolean).join(" · ") || "—")}
        </div>
        <NewBadge firstSeen={row.first_seen} className="mt-1" />
      </div>
      {/* Motor */}
      <div className="md:col-span-2 text-neutral-200">
        {row.engine_code ? (
          <Highlight text={row.engine_code} query={search} />
        ) : (
          "—"
        )}
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">
          {[
            row.fuel ? alientechFuelLabel(row.fuel) : null,
            row.power_kw ? `${row.power_kw} kW` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>
      {/* ECU */}
      <div className="md:col-span-2">
        {row.permalink ? (
          <a
            href={row.permalink}
            target="_blank"
            rel="noreferrer"
            data-testid="alientech-ecu-link"
            className="group/ecu inline-flex flex-col text-neutral-100 hover:text-[#E13636] transition"
          >
            <span className="flex items-center gap-1">
              <Highlight text={row.ecu_full_name} query={search} />
              <ExternalLink className="w-3 h-3 opacity-0 group-hover/ecu:opacity-100 transition" />
            </span>
          </a>
        ) : (
          <div className="text-neutral-100">
            {row.ecu_full_name ? (
              <Highlight text={row.ecu_full_name} query={search} />
            ) : (
              "—"
            )}
          </div>
        )}
        <div
          className="text-[11px] text-neutral-400 font-mono mt-0.5"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {row.ecu_microprocessor || row.ecu_part_number || ""}
        </div>
      </div>
      {/* Typ */}
      <div className="md:col-span-1 flex flex-col gap-1 items-start">
        {row.ecu_type && (
          <Badge
            variant="outline"
            className="border-neutral-700 text-neutral-300 bg-panel text-[10px] font-mono"
          >
            {ecuTypeLabel(row.ecu_type)}
          </Badge>
        )}
        {flags.clone && (
          <Badge
            variant="outline"
            className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[9px] gap-1"
          >
            <KlonbarIcon className="w-3 h-3" />
            Klonbar
          </Badge>
        )}
      </div>
      {/* Verbindungen */}
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-start">
        <ConnectionBadge label="OBD" connection={connections.obd} />
        <ConnectionBadge label="Bench" connection={connections.bench} />
        <ConnectionBadge label="Boot" connection={connections.boot} />
      </div>
    </div>
  );
}
